import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, ArrowBigUp, Search, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Pill, Loader, Empty } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const TOPICS = ["all", "policy", "cities", "environment", "education", "jobs", "health", "rights"];
const field = "w-full h-11 bg-white/[0.04] border border-white/10 px-4 text-sm outline-none focus:border-neon/60 transition-colors";

const Discussions = () => {
  const { user, login } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState("all");
  const [sort, setSort] = useState("recent");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", topic: "policy", body: "" });

  const load = () => {
    setLoading(true);
    api
      .get("/discussions", { params: { topic, sort, q: q || undefined } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, [topic, sort, q]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post("/discussions", f);
      toast.success("Thread started.");
      setF({ title: "", topic: "policy", body: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const upvote = async (id: string) => {
    if (!user) return login();
    try {
      await api.post(`/discussions/${id}/upvote`);
      setItems(items.map((d) => (d.discussion_id === id ? { ...d, upvotes: d.upvotes + 1 } : d)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16" data-testid="discussions-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">Open floor</span>
          <h1 className="mt-3 font-bebas text-5xl sm:text-6xl tracking-wide uppercase">Discussions</h1>
          <p className="mt-3 text-sm text-white/45">Anyone can read. Sign in to post.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 border border-white/10 px-3 h-11 bg-white/[0.03] focus-within:border-neon/60 transition-colors">
            <Search size={15} className="text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search threads" data-testid="discussions-search" className="bg-transparent outline-none text-sm w-48 placeholder:text-white/30" />
          </div>
          {user ? (
            <button onClick={() => setOpen(!open)} data-testid="new-thread-btn" className="bg-neon px-6 h-11 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] hover:pc-glow transition-shadow duration-200">
              <Plus size={15} /> New thread
            </button>
          ) : (
            <button onClick={login} data-testid="discussions-login-btn" className="border border-white/20 px-6 h-11 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">
              Sign in to post
            </button>
          )}
        </div>
      </div>

      {open && (
        <form onSubmit={create} className="mt-10 border border-white/10 bg-ink-surface p-7 grid gap-4" data-testid="new-thread-form">
          <input required placeholder="What's the question?" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="thread-title-input" className={field} />
          <select value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} data-testid="thread-topic-select" className={field}>
            {TOPICS.filter((t) => t !== "all").map((t) => <option key={t} value={t} className="bg-ink-surface">{t}</option>)}
          </select>
          <textarea required rows={5} placeholder="Give some context…" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="thread-body-input" className="w-full bg-white/[0.04] border border-white/10 p-4 text-sm outline-none focus:border-neon/60 transition-colors" />
          <button data-testid="thread-submit-btn" className="h-11 bg-neon text-[12px] font-bold uppercase tracking-[0.2em] hover:pc-glow transition-shadow">Post thread</button>
        </form>
      )}

      <div className="mt-10 flex flex-wrap items-center gap-2">
        {TOPICS.map((t) => (
          <button key={t} onClick={() => setTopic(t)} data-testid={`discussions-topic-${t}`}
            className={`px-4 h-9 text-[11px] font-bold uppercase tracking-[0.18em] border transition-colors duration-200 ${
              topic === t ? "border-neon text-neon bg-neon/10" : "border-white/12 text-white/50 hover:text-white"
            }`}>
            {t}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          {["recent", "top"].map((s) => (
            <button key={s} onClick={() => setSort(s)} data-testid={`discussions-sort-${s}`}
              className={`px-4 h-9 text-[11px] font-bold uppercase tracking-[0.18em] border transition-colors duration-200 ${
                sort === s ? "border-pcblue text-pcblue" : "border-white/12 text-white/40 hover:text-white"
              }`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <div className="mt-12"><Empty text="No threads here yet. Start the conversation." testid="discussions-empty" /></div>
      ) : (
        <div className="mt-10 divide-y divide-white/8 border-y border-white/10">
          {items.map((d, i) => (
            <div key={d.discussion_id} className="py-6 flex gap-6 group" data-testid={`discussion-row-${i}`}>
              <button onClick={() => upvote(d.discussion_id)} data-testid={`upvote-${i}`}
                className="shrink-0 w-14 h-16 border border-white/10 grid place-items-center hover:border-neon hover:text-neon transition-colors">
                <ArrowBigUp size={18} />
                <span className="font-bebas text-xl tracking-wide">{d.upvotes}</span>
              </button>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <Pill tone="blue">{d.topic}</Pill>
                  <span className="text-[11px] uppercase tracking-[0.18em] text-white/35">{d.author_name}</span>
                </div>
                <Link to={`/discussions/${d.discussion_id}`} className="mt-3 block font-outfit text-lg font-bold group-hover:text-neon transition-colors">
                  {d.title}
                </Link>
                <p className="mt-2 text-sm text-white/50 line-clamp-2">{d.body}</p>
                <div className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/35">
                  <MessageSquare size={12} /> {d.reply_count} replies
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Discussions;
